// Create-event page: the shared form plus Turnstile (human check) and a success
// panel that shows the manage link to save, a QR card, and the public link.

import { ApiError, createEvent, getConfig, uploadAttachment } from '../lib/api';
import { navigate } from '../lib/router';
import { rememberOwned } from '../lib/ownership';
import { renderEventForm } from './eventForm';
import { buildQrCard } from './qrCard';
import type { CreateEventResponse, LocalEventInput } from '../types/event';

// Loose typing for the Turnstile global (loaded from a script tag).
interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  getResponse: (id?: string) => string | undefined;
  reset: (id?: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScript: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (turnstileScript) return turnstileScript;
  turnstileScript = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('failed to load Turnstile'));
    document.head.appendChild(s);
  });
  return turnstileScript;
}

export function renderCreatePage(root: HTMLElement): void {
  root.innerHTML = `
    <section class="page create-page">
      <a class="back-link" href="/" data-link>&larr; Back to events</a>
      <h2>Post an event</h2>
      <p class="page-lead">Free, no account needed. You get a private link to edit or delete it later, plus a printable QR code.</p>
      <div id="create-mount"></div>
    </section>
  `;
  const mount = root.querySelector<HTMLElement>('#create-mount')!;

  // Posting depends on the D1 binding. If it is not enabled yet, show a notice
  // instead of a form that cannot submit.
  void getConfig().then((cfg) => {
    if (!cfg.postingEnabled) {
      mount.innerHTML = `
        <div class="banner info">
          Posting is not switched on yet. The database is being set up. Browsing,
          the calendar, and the map all work in the meantime. Check back soon.
        </div>
        <a class="btn" href="/" data-link>Back to events</a>`;
    }
  });

  const formHandle = renderEventForm({ category: 'community' }, { submitLabel: 'Create event' });
  mount.appendChild(formHandle.element);

  // Image picker. Files are uploaded after the event is created (uploads need
  // the new event id + edit token). Up to 10 files, 20MB total (server-enforced).
  const imageField = document.createElement('label');
  imageField.className = 'field';
  imageField.innerHTML = `
    <span class="field-label">Photos</span>
    <input id="create-images" type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple />
    <span class="field-hint">Optional. Add one or more images for your event (JPG, PNG, WebP, GIF).</span>
    <span class="field-err" id="image-status"></span>
  `;
  formHandle.element.insertBefore(
    imageField,
    formHandle.element.querySelector('.submit-btn')
  );
  const imageInput = imageField.querySelector<HTMLInputElement>('#create-images')!;
  const imageStatus = imageField.querySelector<HTMLElement>('#image-status')!;

  // Turnstile widget container + status line.
  const tsWrap = document.createElement('div');
  tsWrap.className = 'turnstile-wrap';
  const tsBox = document.createElement('div');
  tsBox.id = 'turnstile-box';
  const formError = document.createElement('p');
  formError.className = 'form-error';
  formError.setAttribute('role', 'alert');
  tsWrap.append(tsBox, formError);
  formHandle.element.insertBefore(
    tsWrap,
    formHandle.element.querySelector('.submit-btn')
  );

  let widgetId: string | undefined;
  let siteKey = '';
  void getConfig().then((cfg) => {
    siteKey = cfg.turnstileSiteKey;
    if (!siteKey) {
      tsBox.innerHTML = '<span class="field-hint">Human check disabled (no site key configured).</span>';
      return;
    }
    loadTurnstile()
      .then(() => {
        if (window.turnstile) {
          widgetId = window.turnstile.render(tsBox, { sitekey: siteKey, theme: 'dark' });
        }
      })
      .catch(() => {
        tsBox.innerHTML = '<span class="field-err">Could not load the human check. Reload to try again.</span>';
      });
  });

  formHandle.element.addEventListener('submit', async (e) => {
    e.preventDefault();
    formError.textContent = '';
    const { value, errors } = formHandle.read();
    if (!value) {
      formHandle.showErrors(errors);
      return;
    }

    let token = 'dev';
    if (siteKey) {
      token = window.turnstile?.getResponse(widgetId) ?? '';
      if (!token) {
        formError.textContent = 'Please complete the human check above.';
        return;
      }
    }

    formHandle.setBusy(true);
    try {
      const created = await createEvent(value, token);
      rememberOwned({
        id: created.id,
        name: value.name,
        manageUrl: created.manageUrl,
        publicUrl: created.publicUrl,
        savedAt: new Date().toISOString(),
      });

      // Upload any selected images now that we have the id + edit token.
      const files = Array.from(imageInput.files ?? []);
      if (files.length > 0) {
        imageStatus.textContent = `Uploading ${files.length} photo${files.length > 1 ? 's' : ''}...`;
        let failed = 0;
        for (const file of files) {
          try {
            await uploadAttachment(created.id, file, created.editToken);
          } catch {
            failed++;
          }
        }
        if (failed > 0) {
          imageStatus.textContent = `${failed} photo(s) could not be uploaded. You can add them later from the manage page.`;
        }
      }

      showSuccess(root, created, value);
    } catch (err) {
      formHandle.setBusy(false);
      if (siteKey && window.turnstile) window.turnstile.reset(widgetId);
      if (err instanceof ApiError) {
        if (err.fields) formHandle.showErrors(err.fields);
        formError.textContent = err.message;
      } else {
        formError.textContent = 'Something went wrong. Please try again.';
      }
    }
  });
}

function showSuccess(
  root: HTMLElement,
  created: CreateEventResponse,
  input: LocalEventInput
): void {
  root.innerHTML = `
    <section class="page success-page">
      <h2>Your event is live</h2>
      <div class="banner warn save-banner">
        <strong>Save this manage link.</strong> It is the only way to edit or delete your
        event later. We saved it on this device, but if you clear your browser or switch
        devices you will need it. Only the site admin can recover it otherwise.
      </div>
      <div class="manage-link-row">
        <input id="manage-url" type="text" readonly value="${escapeAttr(created.manageUrl)}" />
        <button id="copy-manage" class="btn" type="button">Copy</button>
      </div>
      <div id="qr-mount" class="qr-mount"></div>
      <div class="success-actions">
        <a class="btn primary" href="/e/${created.id}" data-link>View event page</a>
        <a class="btn" href="/create" data-link>Post another</a>
        <a class="btn ghost" href="/" data-link>Back to events</a>
      </div>
    </section>
  `;

  root.querySelector<HTMLElement>('#qr-mount')!.appendChild(
    buildQrCard({ name: input.name, start: input.start, publicUrl: created.publicUrl })
  );

  const copyBtn = root.querySelector<HTMLButtonElement>('#copy-manage')!;
  copyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(created.manageUrl);
      copyBtn.textContent = 'Copied';
      setTimeout(() => (copyBtn.textContent = 'Copy'), 1500);
    } catch {
      const input = root.querySelector<HTMLInputElement>('#manage-url')!;
      input.select();
    }
  });

  // Wire the data-link buttons (router intercepts globally, but ensure here too).
  root.querySelectorAll<HTMLAnchorElement>('a[data-link]').forEach((a) => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      navigate(a.getAttribute('href')!);
    });
  });
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
